/** Request/response types for in-app account deletion (store compliance). */

import { describeApiError, purgeAccountServerData } from '../services/scanGradingApi';

export type AccountDeletionRequest =
  | { step: 'initial' }
  | { step: 'password'; password: string }
  | { step: 'apple' };

export type AccountDeletionResult =
  | { status: 'success' }
  | { status: 'need_password' }
  | { status: 'need_apple' }
  | { status: 'error'; message: string };

/** Outcome of the server-side purge step that precedes Firebase deletion. */
export type ServerPurgeResult = { ok: true } | { ok: false; retryable: boolean; message: string };

/**
 * Step 1 of account deletion: purge the user's server-side data via
 * `DELETE /api/governance/account` BEFORE any irreversible Firebase
 * account deletion or local-state clearing.
 *
 * Returns `{ ok: true }` only when the backend confirms the purge. On
 * failure it returns a RETRYABLE error so the caller can surface a
 * "try again" message and MUST NOT proceed to Firebase deletion or
 * report the account as deleted — the teacher's data may still be on
 * the server.
 *
 * The purge fn is injectable for testing; defaults to the real client.
 */
export async function purgeServerAccountData(
  purgeFn: () => Promise<{ ok: boolean }> = purgeAccountServerData
): Promise<ServerPurgeResult> {
  try {
    const res = await purgeFn();
    if (res && res.ok) return { ok: true };
    return {
      ok: false,
      retryable: true,
      message:
        'We could not confirm your data was deleted from the server. Please try again — your account was not deleted.',
    };
  } catch (err) {
    // A purge failure is ALWAYS retryable: re-attempting deletion is safe
    // (the backend purge is idempotent) and the account was not deleted,
    // so the user must be able to try again. We only borrow the
    // classifier's friendlier wording when available.
    let message =
      'We could not delete your data from the server. Please check your connection and try again — your account was not deleted.';
    try {
      const described = describeApiError(err, { action: 'delete' });
      if (described.userMessage) {
        message = `${described.userMessage} Your account was not deleted.`;
      }
    } catch {
      /* fall back to the generic retryable message above */
    }
    return { ok: false, retryable: true, message };
  }
}
